import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppCard,
  AppDatePicker,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import ModuleComsaEnvironmentalIndustrialControlCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalIndustrial/ModuleComsaEnvironmentalIndustrialControlCard';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalIndustrialControlCardScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const locale = useSelector((state) => state.global.urlData.lang);
  const [initialValues, setInitialValues] = useState();
  const [filterApplicable, setFilterApplicable] = useState(false);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "industrialControlCard",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "industrialControlCard",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="industrial"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_industrial_control_card" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="environmental.design.control.sheet"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <FilterApplicable
                  className="mt-2 mb-2"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                  onClick={() => setFilterApplicable(!filterApplicable)}
                  $filtered={filterApplicable}
                >
                  <AppRemixIcon icon="filter" className="mr-2" size={16} />
                  {filterApplicable ? (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="remove.applicable.filter"
                    />
                  ) : (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                </FilterApplicable>
                <AppCard
                  header={
                    <span>
                      <Translator
                        file="module-comsaEnvironmental"
                        name="general.data.work"
                      />
                    </span>
                  }
                  className="mb-2"
                >
                  <AppDatePicker
                    title={
                      <Translator file="module-intelligence" name="date" />
                    }
                    locale={locale}
                    value={values.date}
                    error={errors.date}
                    onChange={(opt) => setFieldValue("date", opt)}
                    placeholder={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="enter.date"
                      />
                    )}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                  />
                </AppCard>
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="atmospheric.emissions"
                    />
                    .{" "}
                    <Translator
                      file="module-comsaEnvironmental"
                      name="emission.particles.combustion.gases"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[0]}
                  setFieldValue={(n, v) => setFieldValue(`0.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="climatic.study.site"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[1]}
                  setFieldValue={(n, v) => setFieldValue(`1.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.ventilation.systems.indoor.air.treatment.thermal.energy.recovery"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[2]}
                  setFieldValue={(n, v) => setFieldValue(`2.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.thermal.insulation.buildings.review.thermal.bridges"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[3]}
                  setFieldValue={(n, v) => setFieldValue(`3.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="measures.protection.solar.radiation.sunshades.glass.etc"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[4]}
                  setFieldValue={(n, v) => setFieldValue(`4.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="reduction.greenhouse.gas.refrigerants.thermal.energy.production"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[5]}
                  setFieldValue={(n, v) => setFieldValue(`5.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.prefabrication.building.elements.avoid.onsite.welding"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[6]}
                  setFieldValue={(n, v) => setFieldValue(`6.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="measures.reduce.lighting.impact"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[7]}
                  setFieldValue={(n, v) => setFieldValue(`7.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="recommendation.use.vehicles.machinery.low.gas.emissions"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="noise.vibrations"
                    />
                    .{" "}
                    <Translator file="module-comsaEnvironmental" name="noise" />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[8]}
                  setFieldValue={(n, v) => setFieldValue(`8.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="conducting.acoustic.studies.noise.map"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[9]}
                  setFieldValue={(n, v) => setFieldValue(`9.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.materials.low.acoustic.emission"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[10]}
                  setFieldValue={(n, v) => setFieldValue(`10.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.machinery.equipment.low.sonic.level"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[11]}
                  setFieldValue={(n, v) => setFieldValue(`11.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.noisy.activities.daytime.hours"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="liquid.discharges"
                    />
                    .{" "}
                    <Translator
                      file="module-comsaEnvironmental"
                      name="water.discharges"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[12]}
                  setFieldValue={(n, v) => setFieldValue(`12.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishment.cleaning.equipment.maintenance.areas"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[13]}
                  setFieldValue={(n, v) => setFieldValue(`13.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.appropriate.waste.fuel.storage.areas"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[14]}
                  setFieldValue={(n, v) => setFieldValue(`14.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="design.water.purification.systems"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.consumption.raw.materials"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[15]}
                  setFieldValue={(n, v) => setFieldValue(`15.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.sustainable.materials.products"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.water.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[16]}
                  setFieldValue={(n, v) => setFieldValue(`16.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.water.consumption"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[17]}
                  setFieldValue={(n, v) => setFieldValue(`17.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.low.consumption"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[18]}
                  setFieldValue={(n, v) => setFieldValue(`18.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.water.reuse.separation.rainwater.wastewater"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.energy.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[19]}
                  setFieldValue={(n, v) => setFieldValue(`19.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.clean.energies.solar.thermal.geothermal.solar"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[20]}
                  setFieldValue={(n, v) => setFieldValue(`20.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.fossil.fuel.consumption"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[21]}
                  setFieldValue={(n, v) => setFieldValue(`21.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.low.consumption.machinery.equipment"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[22]}
                  setFieldValue={(n, v) => setFieldValue(`22.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.energy.consumption.electrical.systems"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[23]}
                  setFieldValue={(n, v) => setFieldValue(`23.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.free.cooling.systems"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[24]}
                  setFieldValue={(n, v) => setFieldValue(`24.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="devices.energy.consumption.control.detectors.energy.etc"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[25]}
                  setFieldValue={(n, v) => setFieldValue(`25.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.low.electrical.consumption"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.urban.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[26]}
                  setFieldValue={(n, v) => setFieldValue(`26.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.waste.choice.materials"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.inert.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[27]}
                  setFieldValue={(n, v) => setFieldValue(`27.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.waste.separation"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.non.hazardous.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[28]}
                  setFieldValue={(n, v) => setFieldValue(`28.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.reuse.valorization.disposal.waste"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.hazardous.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[29]}
                  setFieldValue={(n, v) => setFieldValue(`29.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.construction.demolition.waste.management"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="order.cleanliness"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[30]}
                  setFieldValue={(n, v) => setFieldValue(`30.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="clean.materials.not.generate.dust.waste"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[31]}
                  setFieldValue={(n, v) => setFieldValue(`31.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="clean.construction.methods.establishing.cleaning.systems"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="landscape"
                    />
                    .{" "}
                    <Translator
                      file="module-comsaEnvironmental"
                      name="visual.impact"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[32]}
                  setFieldValue={(n, v) => setFieldValue(`32.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="identify.areas.high.landscape.quality"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[33]}
                  setFieldValue={(n, v) => setFieldValue(`33.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="recommendation.storage.areas"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <ModuleComsaEnvironmentalTitleCard color="primary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="impact.mobility"
                    />
                    .{" "}
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.impact"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalIndustrialControlCard
                  values={values?.[34]}
                  setFieldValue={(n, v) => setFieldValue(`34.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.closures.diversions.during.construction.phase"
                    />
                  }
                  filterApplicable={filterApplicable}
                />
                <div className="mb-3" />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;

const FilterApplicable = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #28a745;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #28a745;
  color: ${(props) => (props.$filtered ? '#28a745' : '#fff')};
  background-color: ${(props) => (props.$filtered ? '#fff' : '#28a745')};
`;
